/**
 * @file api-site-pages.ts
 * @description Клиент `/admin/site/*` — page-builder (phase19.8; phase19.16
 * Р2 — контракт site-view-api-contract.md §16 «Страницы из секций v2»).
 *
 * Зеркало apps/api/src/site-pages/admin-site-pages.controller.ts.
 * Секция (`SECTION`) — всегда payload v2 из shared (`SectionPayload`,
 * `payloadVersion: 2`). Прежние 14 типов и секция v1 удалены из кабинета
 * (phase19.16 Р2 «чистка», TD-327, SB107): блок другого типа, если он ещё
 * есть в данных, можно только удалить.
 *
 * SB120 этап 3а (db-design-sb120-stage3 §7): создание и правка блока с
 * payload всегда шлют `payloadVersion: SECTION_PAYLOAD_VERSION` — признак
 * нового кабинета для моста записи сервера (TD-412: без признака сервер
 * считает тело старой вкладкой и поднимает его). Версия новее кода сервера —
 * 409 `section_format_outdated` («Обновите страницу», block-errors.ts).
 *
 * Р3а (v0.19.61; SB126–SB128): страница несёт состояние публикации (`live`,
 * `draft`, `liveVersion`, `liveSlug`, `hasDraftChanges`, `deletedAt`).
 * Публикация, «Отменить правки», версии, удаление/восстановление и «Сделать
 * главной» — клиент `api-site-page-publish.ts` (там же отпечаток черновика).
 */

import {
  SECTION_PAYLOAD_VERSION,
  type SectionPayload as SectionPayloadV2,
  type SitePageBackground,
  type SitePageBreadcrumbs,
  type SiteSlotKey,
} from "@chaiberi/shared";

import type { SeoKeywordsDto, SeoKeywordsInput } from "./site-seo/keywords";
import { apiFetch } from "./api-client";
import type { SitePageDraftState, SitePageLive } from "./api-site-page-publish";

// ─── Блоки ───────────────────────────────────────────────────────────────

/**
 * Тип блока страницы: только секция (phase19.16 Р2 «чистка», TD-327, SB107).
 * Прежние 14 типов удалены из кабинета; блок другого типа в данных до
 * удаления из базы приходит строкой — кабинет показывает его
 * «неподдерживаемым» (`isSectionBlock` в block-meta).
 */
export type SiteBlockType = "SECTION";

/** phase19.8.5 → 19.8.6 — эффект появления блока при скролле. */
export type BlockEffect =
  | "none"
  | "fade"
  | "fade-up"
  | "fade-down"
  | "fade-left"
  | "fade-right"
  | "zoom"
  | "zoom-out"
  | "blur"
  | "blur-up"
  | "flip-up"
  | "rotate";

/** Кривые перехода; spring — cubic-bezier с лёгким перелётом. */
export type BlockEffectEasing = "ease" | "ease-out" | "ease-in-out" | "spring";

/** phase19.8.5 → 19.8.6 — визуальные настройки блока (общие для всех типов). */
export interface BlockAppearance {
  effect?: BlockEffect;
  /** Задержка старта, мс (0–5000). */
  delayMs?: number;
  /** Длительность перехода, мс (100–5000); дефолт 900. */
  durationMs?: number;
  /** Сдвиг направленных эффектов, px (8–200); дефолт 32. */
  distancePx?: number;
  easing?: BlockEffectEasing;
}

/** TEMPLATE — шаблон страницы товара (Р2а, `_template-product`); в списке страниц его нет. */
export type SitePageKind = "SYSTEM_HOME" | "CUSTOM" | "SLOT" | "TEMPLATE";

// ─── DTO страниц/блоков ──────────────────────────────────────────────────

export interface SitePageListItem {
  id: string;
  slug: string;
  title: string;
  kind: SitePageKind;
  isPublished: boolean;
  publishedAt: string | null;
  updatedAt: string;
  blocksCount: number;
  /** Место страницы-места; у обычных страниц `null` (§16.3). */
  slot?: SiteSlotKey | null;
  designId?: string | null;
  /** Р3а: номер версии на сайте; `null` — страница ещё не была на сайте. */
  liveVersion?: number | null;
  /** Р3а: адрес на сайте (адрес живой версии); `null` — не на сайте. */
  liveSlug?: string | null;
  /** Р3а: есть неопубликованные правки (быстрый признак; точно — `changes(id)`). */
  hasDraftChanges?: boolean;
  /** Р3а (SB128): когда удалена; `null` — не удалена. */
  deletedAt?: string | null;
  /** v0.19.65 (SB146): хлебные крошки страницы в черновике; сервер до v0.19.65 поля не отдаёт. */
  breadcrumbs?: SitePageBreadcrumbs;
}

/** Замечание к тексту блока (§16.6): сохранение не блокирует. */
export interface SiteTextWarning {
  /** Путь поля: `columns.items.0.elements.1.text`, `header.title`. */
  path: string;
  text: string;
  term: string;
  kind: "medical" | "superlative" | "foreign";
  message: string;
}

export interface SitePageBlock {
  id: string;
  /** `SECTION`; другое — неподдерживаемый блок из данных (только удалить). */
  type: SiteBlockType | (string & {});
  sortOrder: number;
  isEnabled: boolean;
  /** SECTION — всегда 2; прежние типы — как в базе (§16.1). */
  payloadVersion?: number;
  /** Секция — payload v2 (shared); у неподдерживаемого блока — как в данных. */
  payload: SectionPayloadV2 | Record<string, unknown>;
  /** phase19.8.5 — визуальные настройки ({ effect }); null — дефолт. */
  appearance: BlockAppearance | null;
  /** Прежний тип блока — неподдерживаемый (только удалить). */
  legacy?: boolean;
  mediaIds?: string[];
  /** Что починено при чтении (v1 → v2, битые поля). */
  warnings?: Array<{ path: string; message: string }>;
  /**
   * §16.1: id элемента «Товары»/«Категории»/«Статьи»/«Отзывы» → сколько
   * записей подходит сейчас (без limit, по сохранённым настройкам).
   */
  dataCounts?: Record<string, number>;
  updatedAt: string;
}

/** Ответ сохранения блока: блок и предупреждения о текстах (§16.1, §16.6). */
export interface SavedSiteBlock extends SitePageBlock {
  textWarnings?: SiteTextWarning[];
}

export interface SitePageDetail extends SeoKeywordsDto {
  id: string;
  slug: string;
  title: string;
  kind: SitePageKind;
  isPublished: boolean;
  publishedAt: string | null;
  updatedAt: string;
  seoTitle: string | null;
  seoDescription: string | null;
  noIndex: boolean;
  /** v0.19.65 (SB146): «как на сайте / показывать / скрыть»; нет — «как на сайте». */
  breadcrumbs?: SitePageBreadcrumbs;
  /** §16.3 — место страницы-места; `null` у обычных. */
  slot?: SiteSlotKey | null;
  /** §19.9 — шаблон страницы-шаблона (`kind: "TEMPLATE"`); `null` у прочих. */
  template?: "PRODUCT" | null;
  /** Свой фон страницы; `null` — «как у сайта». */
  background?: SitePageBackground | null;
  /** Дизайн страницы; `null` — общий вид сайта. */
  designId?: string | null;
  design?: { id: string; name: string } | null;
  blocksLimit?: { used: number; max: number };
  references?: Array<{ kind: "delivery_terms"; label: string }>;
  blocks: SitePageBlock[];
  /**
   * SB120 этап 1: файлы блоков и фона страницы — id → адрес, размеры, тип
   * (как `media` публичного ответа; API `SitePageMediaDto`). Нужна для рамки
   * кадра, «ширина ÷ пропорция = высота» и предупреждений о размере фото.
   * Нет поля (старый API) — кабинет меряет картинку сам.
   */
  media?: Record<string, SitePageMedia>;
  /** Р3а: версия на сайте; `null` — ещё не была на сайте. */
  live?: SitePageLive | null;
  /** Р3а: черновик — счётчик правок, кто и когда правил, «правит другой». */
  draft?: SitePageDraftState;
  liveVersion?: number | null;
  liveSlug?: string | null;
  hasDraftChanges?: boolean;
  deletedAt?: string | null;
}

/** Файл страницы в ответе кабинета (`SitePageMediaDto`); `width/height` — null у части SVG. */
export interface SitePageMedia {
  url: string;
  alt: string | null;
  width: number | null;
  height: number | null;
  /** Готовые ширины вариантов (`?w=`); пусто — вариантов нет. */
  variantWidths: number[];
  mimeType: string;
}

export interface CreateSitePageInput {
  slug: string;
  title: string;
  seoTitle?: string;
  seoDescription?: string;
  noIndex?: boolean;
}

export interface UpdateSitePageInput extends SeoKeywordsInput {
  slug?: string;
  title?: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
  noIndex?: boolean;
  /** v0.19.65 (SB146): хлебные крошки страницы — в черновик, на сайт после публикации. */
  breadcrumbs?: SitePageBreadcrumbs;
  /** §16.3: свой фон страницы; `null` — «как у сайта». */
  background?: SitePageBackground | null;
  /** §16.3: дизайн страницы; `null` — общий. */
  designId?: string | null;
}

/** Место для секций в списке (§16.5). */
export interface SiteSlotItem {
  slot: SiteSlotKey;
  /** §19.6: экран конструктора места (блог, каталог). */
  screen?: "blog" | "catalog";
  label: string;
  place: string;
  pageId: string | null;
  isPublished: boolean;
  blocksCount: number;
  /** Р3а: у места есть версия на сайте. */
  hasLive?: boolean;
  /** Р3а: есть неопубликованные правки (быстрый признак). */
  hasDraftChanges?: boolean;
}

/** Новая секция: payload v2, эффект появления и место (после блока, иначе в конец). */
export interface AddSectionInput {
  payload: SectionPayloadV2;
  appearance?: BlockAppearance | null;
  afterBlockId?: string | null;
}

/** Конфиг live-предпросмотра (phase19.8.1); null-поля — не настроен. */
export interface SitePreviewConfig {
  webBaseUrl: string | null;
  /** Подписанный ключ предпросмотра страниц (phase19.16 Р1-6а), живёт час. */
  token: string | null;
  /** Конец срока ключа; `null` — ключа нет. */
  expiresAt: string | null;
}

// ─── Клиент ──────────────────────────────────────────────────────────────

export const sitePages = {
  previewConfig: () => apiFetch<SitePreviewConfig>("/admin/site/preview-config"),
  list: () => apiFetch<SitePageListItem[]>("/admin/site/pages"),
  findOne: (id: string) => apiFetch<SitePageDetail>(`/admin/site/pages/${id}`),
  create: (input: CreateSitePageInput) =>
    apiFetch<SitePageDetail>("/admin/site/pages", { method: "POST", json: input }),
  update: (id: string, input: UpdateSitePageInput) =>
    apiFetch<SitePageDetail>(`/admin/site/pages/${id}`, { method: "PATCH", json: input }),
  // Р3а: публикация, скрытие, удаление и «Сделать главной» — `sitePagePublish` (api-site-page-publish.ts).

  /** §16.2 — новая секция; создавать прежние типы нельзя. */
  addSection: (pageId: string, input: AddSectionInput) =>
    apiFetch<SavedSiteBlock>(`/admin/site/pages/${pageId}/blocks`, {
      method: "POST",
      json: {
        type: "SECTION",
        payload: input.payload,
        payloadVersion: SECTION_PAYLOAD_VERSION,
        ...(input.appearance ? { appearance: input.appearance } : {}),
        ...(input.afterBlockId ? { afterBlockId: input.afterBlockId } : {}),
      },
    }),
  /** §16.2 — секция из заготовки платформы. */
  addFromPreset: (pageId: string, presetKey: string, afterBlockId?: string | null) =>
    apiFetch<SavedSiteBlock>(`/admin/site/pages/${pageId}/blocks/from-preset`, {
      method: "POST",
      json: { presetKey, ...(afterBlockId ? { afterBlockId } : {}) },
    }),
  /** §16.2 — копия блока сразу ниже. */
  duplicateBlock: (blockId: string) =>
    apiFetch<SavedSiteBlock>(`/admin/site/blocks/${blockId}/duplicate`, { method: "POST" }),
  /**
   * §16.2: `expectedUpdatedAt` — `updatedAt` блока, который видел кабинет;
   * блок изменили с тех пор — 409 `block_changed`. Шлётся всегда, после
   * сохранения берётся новый `updatedAt` из ответа.
   */
  updateBlock: (
    blockId: string,
    input: {
      payload?: SectionPayloadV2;
      appearance?: BlockAppearance | null;
      isEnabled?: boolean;
      /** Нет значения — без проверки (ячейка ещё не знает `updatedAt`). */
      expectedUpdatedAt?: string;
    },
  ) =>
    apiFetch<SavedSiteBlock>(`/admin/site/blocks/${blockId}`, {
      method: "PATCH",
      // Признак формата — только вместе с payload (правка вида/порядка его не несёт).
      json: input.payload ? { ...input, payloadVersion: SECTION_PAYLOAD_VERSION } : input,
    }),
  /**
   * §19.3: блок шаблона с обязательными элементами удаляется вместе с их
   * переносом (`moveRequiredTo` — блок-получатель, одной транзакцией).
   */
  removeBlock: (blockId: string, options: { moveRequiredTo?: string } = {}) =>
    apiFetch<void>(`/admin/site/blocks/${blockId}${options.moveRequiredTo ? `?moveRequiredTo=${encodeURIComponent(options.moveRequiredTo)}` : ""}`, { method: "DELETE" }),
  reorderBlocks: (pageId: string, blockIds: string[]) =>
    apiFetch<SitePageBlock[]>(`/admin/site/pages/${pageId}/blocks/reorder`, {
      method: "POST",
      json: { blockIds },
    }),
  /** §16.6 — проверить тексты секции без записи. */
  checkTexts: (payload: SectionPayloadV2) =>
    apiFetch<{ warnings: SiteTextWarning[] }>("/admin/site/blocks/check-texts", {
      method: "POST",
      json: { payload, payloadVersion: SECTION_PAYLOAD_VERSION },
    }),

  /** §16.5 — места для секций (над/под лентой блога, под статьёй). */
  slots: () => apiFetch<SiteSlotItem[]>("/admin/site/slots"),
  /** Страница места; создаётся сервером при первом обращении. */
  slot: (slot: SiteSlotKey) => apiFetch<SitePageDetail>(`/admin/site/slots/${slot}`),
  setSlotPublished: (slot: SiteSlotKey, isPublished: boolean) =>
    apiFetch<SitePageDetail>(`/admin/site/slots/${slot}`, { method: "PUT", json: { isPublished } }),
};

// ─── Витринные отзывы (phase19.8.5) ──────────────────────────────────────

export interface SiteReview {
  id: string;
  title: string;
  body: string;
  author: string;
  photoUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  updatedAt: string;
}

export interface SiteReviewInput {
  title?: string;
  body?: string;
  author?: string;
  photoUrl?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export const siteReviews = {
  list: () => apiFetch<SiteReview[]>("/admin/site/reviews"),
  create: (input: SiteReviewInput) =>
    apiFetch<SiteReview>("/admin/site/reviews", { method: "POST", json: input }),
  update: (id: string, input: SiteReviewInput) =>
    apiFetch<SiteReview>(`/admin/site/reviews/${id}`, { method: "PATCH", json: input }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/site/reviews/${id}`, { method: "DELETE" }),
};
